export type PushLogLevel = 'info' | 'warn' | 'error' | 'success';
export type PushLogCategory = 'detection' | 'prefetch' | 'cache' | 'notification' | 'retry';

export interface PushLog {
  timestamp: number;
  level: PushLogLevel;
  category: PushLogCategory;
  message: string;
  data?: any;
}

const MAX_LOGS = 100;
const STORAGE_KEY = 'pushDebugLogs';

let inMemoryLogs: PushLog[] = [];

function initializeLogs(): void {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      inMemoryLogs = JSON.parse(stored);
      console.log(`[Push Diagnostics] Restored ${inMemoryLogs.length} logs from sessionStorage`);
    }
  } catch (error) {
    console.warn('[Push Diagnostics] Failed to restore logs:', error);
    inMemoryLogs = [];
  }
}

function persistLogs(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(inMemoryLogs));
  } catch (error) {
    console.warn('[Push Diagnostics] Failed to persist logs:', error);
  }
}

export function logPush(
  level: PushLogLevel,
  category: PushLogCategory,
  message: string,
  data?: any
): void {
  const log: PushLog = {
    timestamp: Date.now(),
    level,
    category,
    message,
    data
  };

  inMemoryLogs.push(log);

  if (inMemoryLogs.length > MAX_LOGS) {
    inMemoryLogs = inMemoryLogs.slice(-MAX_LOGS);
  }

  persistLogs();

  const emoji = {
    info: 'ℹ️',
    warn: '⚠️',
    error: '❌',
    success: '✅'
  }[level];

  const consoleMethod = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';
  console[consoleMethod](
    `[Push ${category}] ${emoji} ${message}`,
    data !== undefined ? data : ''
  );
}

export function getPushLogs(): PushLog[] {
  return [...inMemoryLogs];
}

export function clearPushLogs(): void {
  inMemoryLogs = [];
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    console.log('[Push Diagnostics] Logs cleared');
  } catch (error) {
    console.warn('[Push Diagnostics] Failed to clear logs:', error);
  }
}

export function exportPushLogs(): string {
  return JSON.stringify({
    exportedAt: new Date().toISOString(),
    totalLogs: inMemoryLogs.length,
    logs: inMemoryLogs
  }, null, 2);
}

export function getLogStats() {
  const byLevel = inMemoryLogs.reduce((acc, log) => {
    acc[log.level] = (acc[log.level] || 0) + 1;
    return acc;
  }, {} as Record<PushLogLevel, number>);

  const byCategory = inMemoryLogs.reduce((acc, log) => {
    acc[log.category] = (acc[log.category] || 0) + 1;
    return acc;
  }, {} as Record<PushLogCategory, number>);

  return {
    total: inMemoryLogs.length,
    byLevel,
    byCategory,
    oldestLog: inMemoryLogs[0]?.timestamp,
    newestLog: inMemoryLogs[inMemoryLogs.length - 1]?.timestamp
  };
}

initializeLogs();
